/**
 * Home page translations
 * 
 * This file contains translations for the home page of the application.
 */

import { HomeTranslationType } from './types';

// English translations
export const enHome: HomeTranslationType = {
  home: {
    hero: {
      title: "Split Bills",
      subtitle: "Made Simple",
      description: "Track expenses, split costs fairly, and settle debts with friends, family, and colleagues. Real-time syncing keeps everyone on the same page.",
      joinPlaceholder: "Paste an event link or enter its code",
      joinButton: "Join Event",
      joinError: "Invalid event link or code",
      noSignup: "No sign-up needed to join an event",
      or: "or"
    },
    actions: {
      createNewEvent: "Create New Event",
      viewMyEvents: "View My Events",
      eventHistory: "Event History"
    },
    features: {
      title: "Why Choose Split Bill?",
      subtitle: "Everything you need to manage group expenses effortlessly",
      realTimeSync: {
        title: "Real-time Sync",
        description: "All changes sync instantly across all devices. Everyone stays updated automatically."
      },
      smartCalculations: {
        title: "Smart Calculations",
        description: "Automatically calculate who owes whom and minimize the number of transactions."
      },
      mobileOptimized: {
        title: "Mobile Optimized",
        description: "Works perfectly on all devices. Add expenses on the go with our responsive design."
      }
    },
    howItWorks: {
      title: "How It Works",
      subtitle: "Get started in three simple steps",
      step1: {
        title: "Create Event & Invite",
        description: "Start a new event and add participants. Share the link with your group."
      },
      step2: {
        title: "Track Expenses",
        description: "Add expenses as you go. Choose who paid and how to split the cost."
      },
      step3: {
        title: "Settle Up",
        description: "See exactly who owes whom and settle all debts efficiently."
      }
    },
    recentEvents: {
      title: "Recent Events",
      subtitle: "Last visited events",
      viewAll: "View All",
      viewEvent: "View Event",
    },
    tips: {
      label: "Did you know?",
      next: "Another tip",
      goTo: "Go to tip {{index}}",
      items: {
        lockEvent: {
          title: "Lock the event when you are done",
          text: "Once every expense is in, lock the event. That is when transfers start and everyone can mark who has already paid."
        },
        paymentMethod: {
          title: "Two ways to settle up",
          text: "Pick \"One Payment Each\" or \"Single Collector\". As soon as someone marks a transfer as done, the method is locked so nothing shifts around."
        },
        whyThisAmount: {
          title: "Tap an amount to see the maths",
          text: "In Suggested Payments, tap the amount to see exactly which expenses it came from — and the recipient's bank details."
        },
        bankInfo: {
          title: "Add your bank details once",
          text: "Save your account number and QR code in your profile so everyone can transfer to you in a couple of taps."
        },
        sponsor: {
          title: "Someone treating the group?",
          text: "Add it as a sponsorship instead of an expense — the app subtracts it from everyone's share automatically."
        },
        shareLink: {
          title: "Share the link, skip the sign-up",
          text: "Send the event link to your group. Friends can add expenses without creating an account."
        },
        compactMode: {
          title: "Big numbers, small screen",
          text: "Tap the đ|K switch — in the page header, or right inside the amount field — to type and read amounts in thousands."
        },
        export: {
          title: "Keep a copy",
          text: "Export balances and the transfer list to CSV from the Balances and Payments tabs."
        }
      }
    },
    install: {
      button: "Install App",
      guide: {
        title: "Install on your Home Screen",
        subtitle: "Opens in one tap, full screen, like any other app",
        steps: {
          share: {
            action: "Tap",
            label: "Share",
            hint: "Don't see it? Tap the menu button (••• or ≡) next to the address bar first."
          },
          addToHome: {
            action: "Choose",
            label: "Add to Home Screen",
            hint: "Not listed? Scroll down or tap View More."
          },
          confirm: {
            action: "Tap",
            label: "Add",
            hint: "The Split Bill icon then sits on your Home Screen — open the app from there."
          }
        },
        inAppNote: "Opened this from Zalo or Messenger? Open the page in Safari first — apps like these can't add to the Home Screen.",
        gotIt: "Got it",
        close: "Close"
      }
    },
    cta: {
      title: "Ready to Split Bills Smarter?",
      subtitle: "Create an event, share the link, and settle up with your group — completely free",
      button: "Start Splitting Bills",
      benefits: {
        free: "Free to use",
        realTime: "Real-time sync",
        mobile: "Mobile friendly"
      }
    }
  }
};

// Vietnamese translations
export const viHome: HomeTranslationType = {
  home: {
    hero: {
      title: "Chia Hóa Đơn",
      subtitle: "Thật Đơn Giản",
      description: "Theo dõi chi phí, chia sẻ công bằng và thanh toán nợ với bạn bè, gia đình và đồng nghiệp. Đồng bộ hóa theo thời gian thực giúp mọi người luôn cập nhật.",
      joinPlaceholder: "Dán link sự kiện hoặc nhập mã",
      joinButton: "Tham Gia",
      joinError: "Link hoặc mã sự kiện không hợp lệ",
      noSignup: "Không cần đăng ký để tham gia sự kiện",
      or: "hoặc"
    },
    actions: {
      createNewEvent: "Tạo Sự Kiện Mới",
      viewMyEvents: "Xem Sự Kiện Của Tôi",
      eventHistory: "Lịch Sử Sự Kiện"
    },
    features: {
      title: "Tại Sao Chọn Chia Hóa Đơn?",
      subtitle: "Tất cả những gì bạn cần để quản lý chi phí nhóm một cách dễ dàng",
      realTimeSync: {
        title: "Đồng Bộ Thời Gian Thực",
        description: "Tất cả thay đổi đồng bộ ngay lập tức trên tất cả thiết bị. Mọi người luôn được cập nhật tự động."
      },
      smartCalculations: {
        title: "Tính Toán Thông Minh",
        description: "Tự động tính toán ai nợ ai và giảm thiểu số lượng giao dịch."
      },
      mobileOptimized: {
        title: "Tối Ưu Hóa Cho Di Động",
        description: "Hoạt động hoàn hảo trên tất cả thiết bị. Thêm chi phí khi di chuyển với thiết kế đáp ứng của chúng tôi."
      }
    },
    howItWorks: {
      title: "Cách Hoạt Động",
      subtitle: "Bắt đầu với ba bước đơn giản",
      step1: {
        title: "Tạo Sự Kiện & Mời",
        description: "Bắt đầu một sự kiện mới và thêm người tham gia. Chia sẻ liên kết với nhóm của bạn."
      },
      step2: {
        title: "Theo Dõi Chi Phí",
        description: "Thêm chi phí khi bạn đi. Chọn ai đã thanh toán và cách chia chi phí."
      },
      step3: {
        title: "Thanh Toán",
        description: "Xem chính xác ai nợ ai và thanh toán tất cả các khoản nợ một cách hiệu quả."
      }
    },
    recentEvents: {
      title: "Sự Kiện Gần Đây",
      subtitle: "Các sự kiện bạn đã xem gần đây",
      viewAll: "Xem Tất Cả",
      viewEvent: "Xem Sự Kiện",
    },
    tips: {
      label: "Bạn có biết?",
      next: "Tip khác",
      goTo: "Xem tip {{index}}",
      items: {
        lockEvent: {
          title: "Ghi xong thì khóa sự kiện",
          text: "Khi đã nhập đủ chi tiêu, bấm khóa sự kiện. Lúc đó mới bắt đầu nhận chuyển khoản và đánh dấu được ai đã chuyển."
        },
        paymentMethod: {
          title: "Có 2 cách thanh toán",
          text: "Chọn \"Mỗi Người 1 Lần\" hoặc \"Qua 1 Người\". Khi đã có người đánh dấu đã chuyển thì cách chia bị khóa để tránh lệch."
        },
        whyThisAmount: {
          title: "Bấm vào số tiền để hiểu vì sao",
          text: "Trong Thanh Toán Đề Xuất, bấm vào số tiền sẽ thấy khoản đó gồm những chi tiêu nào — kèm luôn số tài khoản người nhận."
        },
        bankInfo: {
          title: "Điền tài khoản một lần thôi",
          text: "Lưu số tài khoản và mã QR trong hồ sơ để mọi người chuyển tiền cho bạn chỉ trong vài giây."
        },
        sponsor: {
          title: "Có người bao cả nhóm?",
          text: "Thêm dạng khoản tài trợ thay vì chi tiêu — app tự trừ vào phần phải trả của mọi người."
        },
        shareLink: {
          title: "Gửi link là xong, khỏi đăng ký",
          text: "Chia sẻ link sự kiện cho cả nhóm. Bạn bè ghi chi tiêu được luôn mà không cần tạo tài khoản."
        },
        compactMode: {
          title: "Số dài, màn hình ngắn",
          text: "Bấm nút đ|K — trên thanh đầu trang hoặc ngay trong ô nhập tiền — để nhập và xem số tiền theo đơn vị nghìn."
        },
        export: {
          title: "Lưu lại một bản",
          text: "Xuất CSV số dư và danh sách chuyển khoản ngay trong tab Số Dư và Thanh Toán."
        }
      }
    },
    install: {
      button: "Cài Đặt Ứng Dụng",
      guide: {
        title: "Cài app lên màn hình chính",
        subtitle: "Mở toàn màn hình, như app thật",
        steps: {
          share: {
            action: "Bấm nút",
            label: "Chia sẻ",
            hint: "Không thấy? Bấm nút menu (••• hoặc ≡) cạnh thanh địa chỉ trước."
          },
          addToHome: {
            action: "Chọn",
            label: "Thêm vào MH chính",
            hint: "Chưa thấy thì kéo xuống hoặc bấm Xem thêm."
          },
          confirm: {
            action: "Bấm",
            label: "Thêm",
            hint: "Biểu tượng Split Bill sẽ nằm trên màn hình chính — từ giờ mở app từ đó."
          }
        },
        inAppNote: "Đang mở từ Zalo hay Messenger? Hãy mở trang này bằng Safari trước — các app đó không thêm được vào màn hình chính.",
        gotIt: "Đã hiểu",
        close: "Đóng"
      }
    },
    cta: {
      title: "Sẵn Sàng Chia Hóa Đơn Thông Minh Hơn?",
      subtitle: "Tạo sự kiện, chia sẻ liên kết và tất toán cùng nhóm của bạn — hoàn toàn miễn phí",
      button: "Bắt Đầu Chia Hóa Đơn",
      benefits: {
        free: "Miễn phí sử dụng",
        realTime: "Đồng bộ thời gian thực",
        mobile: "Thân thiện với di động"
      }
    }
  }
};